'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { useVisualViewportHeight } from '@/lib/hooks/useVisualViewportHeight';
import { useResponsiveLayout } from '@/lib/hooks/useResponsiveLayout';
import { useDialogFocus } from '@/lib/hooks/useDialogFocus';
import { useTranslations } from 'next-intl';

/* ================================================================
   Delos Modal — 居中弹窗 · glass-strong + backdrop
   ================================================================ */

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** 可选的无障碍名称；未传时沿用标题。 */
  ariaLabel?: string;
  /** 图标关闭按钮的本地化名称，由调用方提供。 */
  closeLabel?: string;
  /** 宽度档位 */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** 沉浸式工作区，头部与底部保持可见。 */
  fullScreen?: boolean;
  headerLeading?: React.ReactNode;
  children: React.ReactNode;
  /** 独立于滚动内容的操作区。 */
  footer?: React.ReactNode;
  className?: string;
  /** 调用方可独立调整内容区留白。 */
  contentClassName?: string;
  /** 禁止点击背景关闭 */
  persistent?: boolean;
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  '2xl': 'max-w-5xl',
};

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  ariaLabel,
  closeLabel,
  size = 'md',
  fullScreen = false,
  headerLeading,
  children,
  footer,
  className,
  contentClassName,
  persistent = false,
}: ModalProps) {
  const commonT = useTranslations('common');
  const [portalReady, setPortalReady] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const { isMobile } = useResponsiveLayout();
  const viewportHeight = useVisualViewportHeight(isOpen && (fullScreen || isMobile));

  useBodyScrollLock(isOpen);
  useDialogFocus(panelRef, isOpen && portalReady, persistent ? undefined : onClose);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  if (!portalReady) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          style={viewportHeight ? { height: viewportHeight, bottom: 'auto' } : undefined}
          className={cn(
            'fixed inset-0 z-[220] flex overflow-y-auto',
            fullScreen ? 'items-stretch' : isMobile ? 'items-end justify-stretch px-0 pt-10' : 'items-start justify-center p-4 pt-8 sm:items-center sm:p-6'
          )}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            className="absolute inset-0 bg-[#203644]/25 backdrop-blur-sm"
            onClick={persistent ? undefined : onClose}
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            data-studio-surface
            tabIndex={-1}
            initial={reduceMotion ? false : isMobile ? { opacity: 0, y: 28 } : { opacity: 0, scale: 0.96, y: 8 }}
            animate={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={isMobile ? { opacity: 0, y: 20 } : { opacity: 0, scale: 0.97, y: 4 }}
            transition={{ duration: reduceMotion ? 0 : 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            style={viewportHeight ? fullScreen ? { height: viewportHeight } : { maxHeight: Math.max(0, viewportHeight - 40) } : undefined}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel ?? title}
            className={cn(
              'relative w-full border border-hr-border bg-white shadow-[var(--studio-shadow-overlay)] overflow-y-auto delos-scrollbar focus:outline-none',
              fullScreen ? 'flex h-dvh max-h-dvh flex-col overflow-hidden border-0 rounded-none' : isMobile
                ? 'mt-auto max-h-[min(92dvh,calc(100dvh-12px))] rounded-t-[28px] rounded-b-none border-b-0 px-0 pb-[calc(env(safe-area-inset-bottom)+8px)]'
                : 'my-auto max-h-[85vh] rounded-2xl',
              !fullScreen && !isMobile && sizeMap[size],
              className,
              footer && 'flex flex-col overflow-hidden'
            )}
          >
            {isMobile && !fullScreen && (
              <div className="sticky top-0 z-[1] flex justify-center border-b border-hr-border-dim bg-white/96 px-5 py-3 backdrop-blur">
                <div className="h-1.5 w-12 rounded-full bg-black/10" />
              </div>
            )}

            {/* Header */}
            {(title || subtitle) && (
              <div className={cn('shrink-0', fullScreen ? 'grid grid-cols-[44px_minmax(0,1fr)_44px] sm:grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] sm:px-8 lg:px-11' : ['flex items-start justify-between', isMobile ? 'px-5 py-4' : 'p-6 pb-0'])}>
                {fullScreen && <div className="min-w-0 justify-self-start">{headerLeading}</div>}
                <div className={cn(fullScreen && 'min-w-0 text-center [overflow-wrap:anywhere]')}>
                  {title && <h2 className={cn('font-semibold text-hr-text-primary', fullScreen ? 'text-base sm:text-2xl' : 'text-lg')}>{title}</h2>}
                  {subtitle && <p className="text-[13px] text-hr-text-secondary mt-0.5">{subtitle}</p>}
                </div>
                <button
                  type="button"
                  data-studio-control
                  onClick={onClose}
                  aria-label={closeLabel ?? commonT('close')}
                  disabled={fullScreen && persistent}
                  className={cn('grid size-11 shrink-0 place-items-center rounded-full hover:bg-surface-hover text-hr-text-secondary transition-colors', fullScreen ? 'justify-self-end disabled:opacity-40' : '-mr-2 -mt-2')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className={cn(fullScreen ? 'min-h-0 flex-1 px-4 pb-4 sm:px-8 lg:px-11' : isMobile ? 'px-5 pb-5 pt-4' : 'p-6', !fullScreen && (title || subtitle) && isMobile && 'pt-0', footer && 'min-h-0 overflow-y-auto delos-scrollbar', contentClassName)}>
              {children}
            </div>
            {footer && (
              <div className={cn('shrink-0 border-t border-hr-border-dim bg-white', fullScreen ? 'px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-4 sm:px-8 lg:px-11' : isMobile ? 'px-5 py-4' : 'px-6 py-4')}>
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
